/**
 * AppAttributeDialogs — Phase 2.2 (Plan §8) App.tsx slice #2.
 *
 * Extracts the 5-dialog attribute cluster from `App.tsx`:
 *   - ATTEXT  (AttributeExtractionDialog) — owned by `useEattextDialog`
 *   - ATTDEF  (AttributeDefinitionDialog) — owned by `useAttributeDialogs.attdef`
 *   - BATTMAN (BlockAttributeManagerDialog) — owned by `useBattmanDialog`
 *   - BPM     (BlockPropertiesManagerDialog) — owned by `useBpmDialog`
 *   - ATTEDIT (EditAttributesDialog) — owned by `useAttributeDialogs.editAttributes`
 *   - EATTEDIT (EnhancedAttributeEditorDialog) — owned by `useAttributeDialogs.eattedit`
 *
 * Together they cover the AutoCAD attribute-editing workflow surface.
 * Hook ownership stays in `App.tsx`; this component receives the hook
 * results + lazy chunk refs + the `resolveSelectedInsertForAttributeEditing`
 * resolver as props.
 */

import { Suspense, type ComponentType } from "react";

import type {
  BlockDefinitionRuntime,
  BlockInsertEntityRuntime,
  DrawingDocument,
} from "@dxf-ai-editor/drawing-model";

import type { UseAttributeDialogsResult } from "../hooks/useAttributeDialogs";
import type { UseBattmanDialogResult } from "../hooks/useBattmanDialog";
import type { UseBpmDialogResult } from "../hooks/useBpmDialog";
import type { UseEattextDialogResult } from "../hooks/useEattextDialog";

interface ResolvedInsert {
  insert: BlockInsertEntityRuntime;
  blockDef: BlockDefinitionRuntime;
}

interface AttributeDefinitionProps {
  onConfirm: UseAttributeDialogsResult["attdef"]["props"]["onConfirm"];
  onCancel: UseAttributeDialogsResult["attdef"]["props"]["onCancel"];
  textStyles: DrawingDocument["styleTables"] extends infer S
    ? S extends { textStyles?: infer T }
      ? NonNullable<T>
      : never
    : never;
  defaultTextHeight: number;
}

interface EditAttributesProps {
  insert: BlockInsertEntityRuntime;
  blockDefinition: BlockDefinitionRuntime;
  onConfirm: UseAttributeDialogsResult["editAttributes"]["props"]["onConfirm"];
  onCancel: UseAttributeDialogsResult["editAttributes"]["props"]["onCancel"];
}

interface EnhancedAttributeProps {
  insert: BlockInsertEntityRuntime;
  blockDefinition: BlockDefinitionRuntime;
  textStyles: AttributeDefinitionProps["textStyles"];
  layers: DrawingDocument["styleTables"] extends infer S
    ? S extends { layers?: infer L }
      ? NonNullable<L>
      : never
    : never;
  onApply: UseAttributeDialogsResult["eattedit"]["props"]["onApply"];
  onConfirm: UseAttributeDialogsResult["eattedit"]["props"]["onConfirm"];
  onCancel: UseAttributeDialogsResult["eattedit"]["props"]["onCancel"];
}

interface AppAttributeDialogsProps {
  eattextDialog: UseEattextDialogResult;
  attributeDialogs: UseAttributeDialogsResult;
  battmanDialog: UseBattmanDialogResult;
  bpmDialog: UseBpmDialogResult;
  geometryDocument: DrawingDocument;
  resolveSelectedInsertForAttributeEditing: () => ResolvedInsert | null;
  appendCommandFeed: (text: string) => void;
  t: (key: string, params?: Record<string, string>) => string;
  AttributeExtractionDialog: ComponentType<
    UseEattextDialogResult["props"] & object
  >;
  AttributeDefinitionDialog: ComponentType<AttributeDefinitionProps>;
  BlockAttributeManagerDialog: ComponentType<
    UseBattmanDialogResult["props"] & object
  >;
  BlockPropertiesManagerDialog: ComponentType<
    UseBpmDialogResult["props"] & object
  >;
  EditAttributesDialog: ComponentType<EditAttributesProps>;
  EnhancedAttributeEditorDialog: ComponentType<EnhancedAttributeProps>;
}

export function AppAttributeDialogs({
  eattextDialog,
  attributeDialogs,
  battmanDialog,
  bpmDialog,
  geometryDocument,
  resolveSelectedInsertForAttributeEditing,
  appendCommandFeed,
  t,
  AttributeExtractionDialog,
  AttributeDefinitionDialog,
  BlockAttributeManagerDialog,
  BlockPropertiesManagerDialog,
  EditAttributesDialog,
  EnhancedAttributeEditorDialog,
}: AppAttributeDialogsProps): JSX.Element {
  return (
    <>
      {eattextDialog.isOpen && eattextDialog.props ? (
        <Suspense fallback={null}>
          <AttributeExtractionDialog {...eattextDialog.props} />
        </Suspense>
      ) : null}

      {attributeDialogs.attdef.isOpen ? (
        <Suspense fallback={null}>
          <AttributeDefinitionDialog
            onConfirm={attributeDialogs.attdef.props.onConfirm}
            onCancel={attributeDialogs.attdef.props.onCancel}
            textStyles={
              (geometryDocument.styleTables?.textStyles ??
                []) as AttributeDefinitionProps["textStyles"]
            }
            defaultTextHeight={2.5}
          />
        </Suspense>
      ) : null}

      {battmanDialog.isOpen ? (
        <Suspense fallback={null}>
          <BlockAttributeManagerDialog {...battmanDialog.props} />
        </Suspense>
      ) : null}

      {bpmDialog.isOpen ? (
        <Suspense fallback={null}>
          <BlockPropertiesManagerDialog {...bpmDialog.props} />
        </Suspense>
      ) : null}

      {attributeDialogs.editAttributes.isOpen
        ? (() => {
            const resolved = resolveSelectedInsertForAttributeEditing();
            if (!resolved) {
              return (
                <div data-testid="edit-attributes-no-target" hidden>
                  {(() => {
                    attributeDialogs.editAttributes.props.onCancel();
                    appendCommandFeed(t("toast.attedit.no_insert_selected"));
                    return null;
                  })()}
                </div>
              );
            }
            return (
              <Suspense fallback={null}>
                <EditAttributesDialog
                  insert={resolved.insert}
                  blockDefinition={resolved.blockDef}
                  onConfirm={attributeDialogs.editAttributes.props.onConfirm}
                  onCancel={attributeDialogs.editAttributes.props.onCancel}
                />
              </Suspense>
            );
          })()
        : null}

      {attributeDialogs.eattedit.isOpen
        ? (() => {
            const resolved = resolveSelectedInsertForAttributeEditing();
            if (!resolved) {
              return (
                <div data-testid="eattedit-no-target" hidden>
                  {(() => {
                    attributeDialogs.eattedit.props.onCancel();
                    appendCommandFeed(t("toast.attedit.no_insert_selected"));
                    return null;
                  })()}
                </div>
              );
            }
            return (
              <Suspense fallback={null}>
                <EnhancedAttributeEditorDialog
                  insert={resolved.insert}
                  blockDefinition={resolved.blockDef}
                  textStyles={
                    (geometryDocument.styleTables?.textStyles ??
                      []) as EnhancedAttributeProps["textStyles"]
                  }
                  layers={
                    (geometryDocument.styleTables?.layers ??
                      []) as EnhancedAttributeProps["layers"]
                  }
                  onApply={attributeDialogs.eattedit.props.onApply}
                  onConfirm={attributeDialogs.eattedit.props.onConfirm}
                  onCancel={attributeDialogs.eattedit.props.onCancel}
                />
              </Suspense>
            );
          })()
        : null}
    </>
  );
}
